import { COUNTRY_OPTIONS } from "../../Enums/Country.enum";

/**
 * Turning stored keys back into the words the wizard shows for them.
 *
 * The review screen printed the keys themselves — "HeadOfficeDirect" where step 5 reads "Head office
 * invoiced directly", "EndOfMonth" where the picklist reads "End of month", "NZ" where step 1 reads
 * "New Zealand", "DHM" where the laboratory list reads "Douglass Hanly Moir (DHM)" — and those keys
 * carried through into the frozen review and the SOP (bug 5717).
 *
 * Kept here rather than inline so there is one implementation for every section of the review, and so
 * the fallback behaviour is pinned by tests: a key with no matching option prints as itself, because
 * showing something the reader can act on beats showing nothing at all.
 */
export const referenceLabel = (
  options: { key: string; label: string }[] | null | undefined,
  key: string | null | undefined,
): string | null => {
  if (!key) return null;
  return options?.find((option) => option.key === key)?.label ?? key;
};

/** "New Zealand", not "NZ" — the review shows what step 1 shows (bug 5717). */
export const countryLabel = (code: string | null | undefined): string | null => {
  if (!code) return null;
  return COUNTRY_OPTIONS.find((option) => option.value === code)?.label ?? code;
};

/**
 * A split Xero key's allocation, in words a reader can act on without opening step 5 (bug 5854).
 *
 * "2 service categories / 1 offices" named nothing and got the plural wrong at one. This names the
 * categories and offices themselves — the same treatment the single-key shortcut already gives
 * "all services / all offices" — and falls back to a correctly-pluralised count only if a name
 * cannot be resolved (a category or site removed after the key was scoped to it).
 */
export const xeroKeyAllocationSummary = (
  key: { allServices?: boolean; allOffices?: boolean; serviceCategories?: string[]; siteIDs?: number[] },
  serviceCategoryOptions: { key: string; label: string }[],
  sites: { companySiteID: number; officeName?: string | null }[],
): string => {
  const services = key.allServices
    ? "all services"
    : namedList(
        key.serviceCategories,
        (categoryKey) => referenceLabel(serviceCategoryOptions, categoryKey),
        "service category",
        "service categories",
      );

  const offices = key.allOffices
    ? "all offices"
    : namedList(
        key.siteIDs,
        (siteID) => sites.find((site) => site.companySiteID === siteID)?.officeName ?? null,
        "office",
        "offices",
      );

  return `${services} / ${offices}`;
};

/** Comma-joined names when every id resolves to one; a correctly-pluralised count otherwise. */
const namedList = <T,>(
  ids: T[] | null | undefined,
  resolve: (id: T) => string | null | undefined,
  singular: string,
  plural: string,
): string => {
  const items = ids || [];
  if (!items.length) return `0 ${plural}`;

  const names = items.map(resolve);
  if (names.every((name): name is string => Boolean(name))) return names.join(", ");

  return `${items.length} ${items.length === 1 ? singular : plural}`;
};
